import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Inbox, CalendarClock, CheckCircle2, Video, Check, X, Clock8, AlertCircle, ArrowRight, CalendarPlus, MailQuestion,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatCard, PageHeader, UserAvatar, StatusBadge, EmptyState, Spinner, Pill, SectionCard } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { AddToCalendar } from "@/components/AddToCalendar";
import { toast } from "sonner";
import { fmtDate, fmtTime } from "@/lib/constants";

export default function ExpertDashboard() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/expert/dashboard").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(id + action);
    try { await api.post(`/bookings/${id}/${action}`); toast.success({ accept: t("expert.dashboard.toastConfirmed"), decline: t("expert.dashboard.toastDeclined"), complete: t("expert.dashboard.toastCompleted") }[action]); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  if (!d) return <AppShell><div className="grid place-items-center py-32"><Spinner className="text-primary w-7 h-7" /></div></AppShell>;

  const e = d.expert || {};
  const profileIncomplete = !e.bio || !(e.fields || []).length;

  return (
    <AppShell>
      <PageHeader eyebrow={t("expert.eyebrow")} title={t("expert.dashboard.title")} description={t("expert.dashboard.subtitle")}>
        <Link to="/expert/availability"><Button data-testid="manage-avail-cta" className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform"><CalendarPlus size={16} /> {t("expert.dashboard.manageAvailability")}</Button></Link>
      </PageHeader>

      {e.info_requested && (
        <div className="mb-6 flex items-start gap-3 rounded-[0.7rem] border border-warning/40 bg-warning/5 p-4 animate-fade-up">
          <MailQuestion size={18} className="text-warning shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">{t("expert.dashboard.infoNeededTitle")}</p>
            <p className="text-sm text-muted-foreground whitespace-pre-line mt-1 leading-relaxed">{e.info_request_message || t("expert.dashboard.infoNeededDefault")}</p>
            <Link to="/expert/profile"><Button size="sm" className="rounded-lg gap-1.5 mt-3 active:scale-[0.98] transition-transform">{t("expert.dashboard.updateProfile")} <ArrowRight size={14} /></Button></Link>
          </div>
        </div>
      )}

      {e.status === "pending" && !e.info_requested && (
        <div className="mb-6 flex items-start gap-3 rounded-[0.7rem] border border-warning/30 bg-warning/5 p-4 animate-fade-up">
          <AlertCircle size={18} className="text-warning shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">{t("expert.dashboard.reviewTitle")}</p>
            <p className="text-sm text-muted-foreground">{t("expert.dashboard.reviewDesc")}</p>
          </div>
        </div>
      )}

      {profileIncomplete && (
        <div className="mb-6 flex items-center gap-3 rounded-[0.7rem] border border-primary/30 bg-primary/5 p-4 animate-fade-up">
          <div className="flex-1">
            <p className="text-sm font-medium">{t("expert.dashboard.completeTitle")}</p>
            <p className="text-sm text-muted-foreground">{t("expert.dashboard.completeDesc")}</p>
          </div>
          <Link to="/expert/profile"><Button variant="outline" size="sm" className="rounded-lg gap-1.5 bg-card">{t("expert.dashboard.complete")} <ArrowRight size={14} /></Button></Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="animate-fade-up d1"><StatCard icon={Inbox} label={t("expert.dashboard.pending")} value={d.pending.length} sub={d.pending.length ? t("expert.dashboard.pendingAwaiting") : t("expert.dashboard.pendingCaughtUp")} accent /></div>
        <div className="animate-fade-up d2"><StatCard icon={CalendarClock} label={t("expert.dashboard.upcoming")} value={d.upcoming.length} sub={d.upcoming.length ? t("expert.dashboard.upcomingScheduled") : t("expert.dashboard.upcomingNothing")} /></div>
        <div className="animate-fade-up d3"><StatCard icon={CheckCircle2} label={t("expert.dashboard.completedStat")} value={d.completed_count} sub={t("expert.dashboard.completedSub")} /></div>
        <div className="animate-fade-up d4"><StatCard icon={Clock8} label={t("expert.dashboard.total")} value={d.total} sub={t("expert.dashboard.totalSub")} /></div>
      </div>

      <div className="mt-6 space-y-6">
        <SectionCard icon={Inbox} title={t("expert.dashboard.requests")} description={t("expert.dashboard.requestsSub")} count={d.pending.length} className="animate-fade-up d2">
          {d.pending.length ? (
            <div className="space-y-3">
              {d.pending.map((b) => (
                <div key={b.booking_id} data-testid={`request-${b.booking_id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-border p-4 transition-colors hover:border-primary/30 hover:bg-secondary/30">
                  <UserAvatar name={b.employee_name} size={44} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{b.employee_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{b.topic}</p>
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <Pill className="bg-primary/10 text-primary">{b.field}</Pill>
                      <span className="text-xs text-muted-foreground">{fmtDate(b.start)} · {fmtTime(b.start)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button data-testid={`decline-${b.booking_id}`} variant="outline" disabled={busy} onClick={() => act(b.booking_id, "decline")} className="rounded-lg gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/5"><X size={15} /> {t("expert.dashboard.decline")}</Button>
                    <Button data-testid={`accept-${b.booking_id}`} disabled={busy} onClick={() => act(b.booking_id, "accept")} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform">{busy === b.booking_id + "accept" ? <Spinner /> : <><Check size={15} /> {t("expert.dashboard.accept")}</>}</Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Inbox} title={t("expert.dashboard.noRequests")} description={t("expert.dashboard.noRequestsDesc")} />
          )}
        </SectionCard>

        <SectionCard icon={CalendarClock} title={t("expert.dashboard.upcomingSessions")} description={t("expert.dashboard.upcomingSessionsSub")} count={d.upcoming.length} className="animate-fade-up d3">
          {d.upcoming.length ? (
            <div className="space-y-3">
              {d.upcoming.map((b) => (
                <div key={b.booking_id} data-testid={`upcoming-${b.booking_id}`} className="flex items-center gap-3 rounded-xl border border-border p-4 transition-colors hover:border-primary/30 hover:bg-secondary/30">
                  <UserAvatar name={b.employee_name} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><p className="text-sm font-medium">{b.employee_name}</p><StatusBadge status={b.status} /></div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{b.topic}</p>
                    <p className="text-xs text-muted-foreground mt-1">{fmtDate(b.start)} · {fmtTime(b.start)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {b.meeting_link && <a href={b.meeting_link} target="_blank" rel="noreferrer" data-testid={`ejoin-${b.booking_id}`}><Button size="sm" className="rounded-lg gap-1.5"><Video size={14} /> {t("expert.dashboard.join")}</Button></a>}
                    <AddToCalendar booking={b} />
                    <Button size="sm" variant="outline" data-testid={`complete-${b.booking_id}`} disabled={busy} onClick={() => act(b.booking_id, "complete")} className="rounded-lg">{t("expert.dashboard.completeAction")}</Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={CalendarClock} title={t("expert.dashboard.nothingScheduled")} description={t("expert.dashboard.nothingScheduledDesc")} />
          )}
        </SectionCard>
      </div>
    </AppShell>
  );
}
